import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

interface ScanProgress {
  phase: 'fetching_profiles' | 'fetching_messages' | 'fetching_files' | 'analyzing' | 'complete';
  current: number;
  total: number;
  percentage: number;
  message: string;
}

interface OrphanedMediaProgressProps {
  progress: ScanProgress;
  isActive: boolean;
  type?: 'scan' | 'delete';
}

const getPhaseLabel = (phase: string): string => {
  const labels: Record<string, string> = {
    fetching_profiles: 'Carregando perfis',
    fetching_messages: 'Carregando mensagens',
    fetching_files: 'Carregando arquivos',
    analyzing: 'Analisando',
    complete: 'Completo',
  };
  return labels[phase] || phase;
};

const OrphanedMediaProgress: React.FC<OrphanedMediaProgressProps> = ({ 
  progress, 
  isActive,
  type = 'scan'
}) => {
  if (!isActive && progress.phase === 'complete') {
    return null;
  }

  const isComplete = progress.phase === 'complete' && progress.percentage === 100;
  const hasError = progress.message.toLowerCase().includes('erro');

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {isActive && !isComplete && (
            <Loader2 className="h-4 w-4 animate-spin text-luxury-red" />
          )}
          {isComplete && !hasError && (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
          {hasError && (
            <AlertCircle className="h-4 w-4 text-destructive" />
          )}
          <span className="font-medium text-foreground">
            {getPhaseLabel(progress.phase)}
          </span>
        </div>
        <span className="text-muted-foreground">
          {progress.percentage}%
        </span>
      </div>

      <Progress 
        value={progress.percentage} 
        className="h-2"
        indicatorClassName={
          hasError 
            ? 'bg-destructive' 
            : isComplete 
            ? 'bg-green-500' 
            : 'bg-luxury-red'
        }
      />

      {progress.message && (
        <p className="text-xs text-muted-foreground">
          {progress.message}
        </p>
      )}

      {progress.total > 0 && progress.phase !== 'complete' && (
        <p className="text-xs text-muted-foreground">
          {progress.current} / {progress.total} {type === 'delete' ? 'arquivos' : 'itens'}
        </p>
      )}
    </div>
  );
};

export default OrphanedMediaProgress;
